import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "./apiClient";
import { streamSse } from "./sse";
import { nonEmptyFilter, type TokenFilter } from "./tokens";

/**
 * Rozsah výběru v seznamu tokenů.
 *
 * Dva režimy v jednom objektu, protože seznam je stránkovaný a „vybrat všech 1 696
 * odpovídajících filtru" nejde vyjádřit seznamem ID — klient ID z ostatních stránek nemá.
 *
 * - `all: false` → vybráno je právě to, co je v `ids`
 * - `all: true` → vybráno je vše, co odpovídá filtru, a `ids` jsou **výjimky** (odškrtnuté)
 *
 * Test příslušnosti je tím pádem prostý XOR.
 */
export interface TokenSelection {
  all: boolean;
  ids: ReadonlySet<string>;
}

export const EMPTY_SELECTION: TokenSelection = { all: false, ids: new Set() };

export const isSelected = (selection: TokenSelection, id: string): boolean =>
  selection.all !== selection.ids.has(id);

export const isSelectionEmpty = (selection: TokenSelection): boolean =>
  !selection.all && selection.ids.size === 0;

/**
 * Kolik je vybráno. V režimu filtru je to odhad ze serverového `totalElements` — množina
 * se od načtení stránky mohla změnit. Autoritativní počet přijde v prvním průběhu běhu.
 */
export const selectionCount = (selection: TokenSelection, total: number): number =>
  selection.all ? Math.max(0, total - selection.ids.size) : selection.ids.size;

export function toggleRow(selection: TokenSelection, id: string): TokenSelection {
  const ids = new Set(selection.ids);
  if (ids.has(id)) ids.delete(id);
  else ids.add(id);
  return { all: selection.all, ids };
}

/** Zaškrtávátko v hlavičce — týká se jen řádků na viditelné stránce. */
export function togglePage(
  selection: TokenSelection,
  pageIds: string[],
  select: boolean,
): TokenSelection {
  const ids = new Set(selection.ids);
  for (const id of pageIds) {
    // V režimu filtru je `ids` seznam výjimek, takže „zaškrtnout“ znamená výjimku odebrat.
    if (select === selection.all) ids.delete(id);
    else ids.add(id);
  }
  return { all: selection.all, ids };
}

export const selectAllMatching = (): TokenSelection => ({ all: true, ids: new Set() });

/** Stav zaškrtávátka v hlavičce pro danou stránku. */
export function pageSelectionState(selection: TokenSelection, pageIds: string[]) {
  const selected = pageIds.filter((id) => isSelected(selection, id)).length;
  return {
    count: selected,
    isSelected: selected > 0 && selected === pageIds.length,
    isIndeterminate: selected > 0 && selected < pageIds.length,
  };
}

// ========== Požadavek ==========

/**
 * Hodnoty, které jdou na drát, jsou zároveň konstanty pro použití v kódu — ať se
 * `"DEACTIVATE"` nepíše rozeseté po komponentách, kde ho překlep neodhalí ani
 * kompilátor, ani test.
 *
 * Vzor `const` objekt + odvozený typ (ne `enum`): zůstává to obyčejný řetězec, takže
 * serializace do JSON i porovnání s odpovědí serveru fungují bez převodu.
 */
export const BulkTokenAction = {
  EXPIRATION: "EXPIRATION",
  TAGS: "TAGS",
  DEACTIVATE: "DEACTIVATE",
  DELETE: "DELETE",
} as const;
export type BulkTokenAction = (typeof BulkTokenAction)[keyof typeof BulkTokenAction];

export const TagsMode = {
  REPLACE: "REPLACE",
  ADD: "ADD",
  REMOVE: "REMOVE",
} as const;
export type TagsMode = (typeof TagsMode)[keyof typeof TagsMode];

/** Pořadí voleb v nabídce režimu tagů (přidat je nejčastější). */
export const TAGS_MODES: readonly TagsMode[] = [TagsMode.ADD, TagsMode.REMOVE, TagsMode.REPLACE];

export const ExpirationMode = {
  SET: "SET",
  CLEAR: "CLEAR",
} as const;
export type ExpirationMode = (typeof ExpirationMode)[keyof typeof ExpirationMode];

export const BulkRunPhase = {
  FORM: "form",
  RUNNING: "running",
  DONE: "done",
} as const;
export type BulkPhase = (typeof BulkRunPhase)[keyof typeof BulkRunPhase];

export const BulkSkipReason = {
  STATE: "STATE",
  MISSING: "MISSING",
} as const;
export type BulkSkipReason = (typeof BulkSkipReason)[keyof typeof BulkSkipReason];

/** Parametry konkrétní akce; server je validuje podle `action`. */
export interface BulkTokenParams {
  mode?: TagsMode | ExpirationMode;
  tags?: string[];
  expiresAt?: string | null;
  expiresInSeconds?: number | null;
  bypassAuthServer?: boolean;
  reason?: string;
}

export interface BulkTokenRequest extends BulkTokenParams {
  action: BulkTokenAction;
  confirmed: boolean;
  selection: {
    tokenIds?: string[];
    filter?: Record<string, string>;
    excludedTokenIds?: string[];
    expectedCount?: number;
  };
}

/**
 * Sestaví tělo požadavku. Filtr prochází sdíleným `nonEmptyFilter`, aby hromadná akce
 * zasáhla **přesně** tu množinu, kterou vypsal seznam.
 */
export function bulkRequest(
  action: BulkTokenAction,
  selection: TokenSelection,
  filter: TokenFilter,
  params: BulkTokenParams = {},
  opts: { confirmed?: boolean; expectedCount?: number } = {},
): BulkTokenRequest {
  const confirmed = opts.confirmed ?? false;
  return {
    action,
    confirmed,
    ...params,
    selection: selection.all
      ? {
          filter: nonEmptyFilter(filter),
          excludedTokenIds: [...selection.ids],
          expectedCount: opts.expectedCount,
        }
      : { tokenIds: [...selection.ids], expectedCount: opts.expectedCount },
  };
}

// ========== Průběh ==========

/** Název SSE události, pod kterou server posílá průběh (`SseEmitter.event().name(...)`). */
export const SSE_EVENT_PROGRESS = "progress";

/** Fáze běhu, jak je posílá server (`BulkTokenProgressEvent.BulkRunStatus`). */
export const BulkRunStatus = {
  STARTED: "STARTED",
  RESOLVING: "RESOLVING",
  RESOLVED: "RESOLVED",
  PROGRESS: "PROGRESS",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  FAILED: "FAILED",
} as const;
export type BulkRunStatus = (typeof BulkRunStatus)[keyof typeof BulkRunStatus];

export interface BulkProgress {
  processed: number;
  total: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

export interface BulkFailure {
  tokenId: string;
  message: string;
}

export interface BulkSkip {
  tokenId: string;
  state: string | null;
  reason: BulkSkipReason;
  block: string | null;
}

export interface BulkResult {
  action: BulkTokenAction;
  total: number;
  succeeded: number;
  failed: number;
  skipped: number;
  durationMs: number;
  cancelled: boolean;
  expectedCount: number | null;
  failures: BulkFailure[];
  skips: BulkSkip[];
  detailsTruncated: boolean;
}

export interface BulkProgressEvent {
  status: BulkRunStatus;
  message: string | null;
  timestamp: string;
  progress: BulkProgress | null;
  result: BulkResult | null;
}

export interface BulkPreview {
  action: BulkTokenAction;
  total: number;
  eligible: number;
  blocked: number;
  blockedByReason: Record<string, number>;
  byState: Record<string, number>;
  confirmationRequired: boolean;
  limit: number;
}

/**
 * Běh hromadné akce nad SSE streamem.
 *
 * Tři věci, které by jinak bolely:
 * - **přerušení** — zavření dialogu nebo odmountování musí stream zavřít, jinak čtečka
 *   běží dál a zapisuje do odmountované komponenty;
 * - **koalescence renderů** — u tisíců tokenů chodí průběh často, překresluje se nejvýš
 *   jednou za snímek;
 * - **invalidace vždy** — i po přerušení nebo pádu spojení je část tokenů už změněná,
 *   takže seznam je tak jako tak zastaralý.
 */
export function useBulkTokenRun() {
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);
  const abortedRef = useRef(false);
  const latest = useRef<BulkProgress | null>(null);
  const frame = useRef(0);

  const [phase, setPhase] = useState<BulkPhase>(BulkRunPhase.FORM);
  const [progress, setProgress] = useState<BulkProgress | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aborted, setAborted] = useState(false);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (frame.current) cancelAnimationFrame(frame.current);
    },
    [],
  );

  const push = useCallback((next: BulkProgress) => {
    latest.current = next;
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      if (latest.current) setProgress(latest.current);
    });
  }, []);

  const start = useCallback(
    async (request: BulkTokenRequest) => {
      abortedRef.current = false;
      abortRef.current = new AbortController();
      setPhase(BulkRunPhase.RUNNING);
      setError(null);
      setResult(null);
      setProgress(null);
      setAborted(false);
      latest.current = null;

      try {
        await streamSse(
          "/admin/api/v1/tokens/bulk",
          request,
          (event) => {
            if (event.event !== SSE_EVENT_PROGRESS || !event.data) return;
            const parsed = JSON.parse(event.data) as BulkProgressEvent;
            if (parsed.progress) push(parsed.progress);
            if (parsed.result) setResult(parsed.result);
            if (parsed.status === BulkRunStatus.FAILED) setError(parsed.message ?? "");
          },
          abortRef.current.signal,
        );
      } catch (e) {
        // Přerušení uživatelem není chyba — poslední známý průběh zůstane na obrazovce.
        if (!abortedRef.current) {
          setError(e instanceof ApiError ? bulkErrorText(e) : (e as Error).message);
        }
      } finally {
        if (frame.current) {
          cancelAnimationFrame(frame.current);
          frame.current = 0;
        }
        if (latest.current) setProgress(latest.current);
        setAborted(abortedRef.current);
        setPhase(BulkRunPhase.DONE);
        void queryClient.invalidateQueries({ queryKey: ["tokens"] });
      }
    },
    [push, queryClient],
  );

  const cancel = useCallback(() => {
    abortedRef.current = true;
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    setPhase(BulkRunPhase.FORM);
    setProgress(null);
    setResult(null);
    setError(null);
    setAborted(false);
  }, []);

  return { phase, progress, result, error, aborted, start, cancel, reset };
}

export type BulkRun = ReturnType<typeof useBulkTokenRun>;

/** Hláška serveru z chybové odpovědi; jinak alespoň stavový kód. */
export function bulkErrorText(e: ApiError): string {
  const body = e.body as { message?: string; error?: string } | null;
  return body?.message ?? body?.error ?? `HTTP ${e.status}`;
}

/** Náhled — kolik tokenů rozsah zahrnuje a kolika z nich to stav nedovolí. */
export function fetchBulkPreview(request: BulkTokenRequest): Promise<BulkPreview> {
  return apiFetch<BulkPreview>("/admin/api/v1/tokens/bulk/preview", {
    method: "POST",
    json: request,
  });
}
